import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, X } from "lucide-react";
import RichTextEditor from '../RichTextEditor'; // Adjust path as needed

const NotificationPopup = ({ notification, hideNotification }) => {
    if (!notification) return null;

    const getNotificationStyles = () => {
        const baseStyles = "fixed top-6 right-6 z-50 max-w-md p-4 rounded-lg shadow-lg border transition-all duration-300 ease-in-out";
        
        switch (notification.type) {
            case 'success':
                return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
            case 'error':
                return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
            case 'info':
                return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
            default:
                return `${baseStyles} bg-gray-50 border-gray-200 text-gray-800`;
        }
    };

    const getIcon = () => {
        switch (notification.type) {
            case 'success':
                return <CheckCircle className="w-5 h-5 text-green-600" />;
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-600" />;
            case 'info':
                return <AlertCircle className="w-5 h-5 text-blue-600" />;
            default:
                return <AlertCircle className="w-5 h-5 text-gray-600" />;
        }
    };

    return (
        <div className={getNotificationStyles()}>
            <div className="flex items-start">
                <div className="mr-3 mt-0.5">
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium break-words">
                        {notification.message}
                    </p>
                </div>
                <button
                    onClick={hideNotification}
                    className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

const NewsModal = ({ article, onClose, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'KGC Innovations',
    category: '',
    tags: [],
    featured_image: '',
    gallery_images: '',
    featured: false,
    status: 'published',
    published_at: '',
    meta_title: '',
    meta_description: ''
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [customTag, setCustomTag] = useState('');
  const [notification, setNotification] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Show notification function
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  // Hide notification function
  const hideNotification = () => {
    setNotification(null);
  };

  // Load article data when editing
  useEffect(() => {
    if (article) {
      loadArticleData(article.id);
    } else {
      // Reset form for new article
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        author: 'KGC Innovations',
        category: '',
        tags: [],
        featured_image: '',
        gallery_images: '',
        featured: false,
        status: 'published',
        published_at: '',
        meta_title: '',
        meta_description: ''
      });
      setSlugManuallyEdited(false);
      setCustomTag('');
    }
  }, [article]);

  const loadArticleData = async (articleId) => {
    try {
      const response = await fetch(`/api/news.php?id=${articleId}`);
      const result = await response.json();
      
      if (result.success) {
        const articleData = result.data;
        setFormData({
          title: articleData.title || '',
          slug: articleData.slug || '',
          excerpt: articleData.excerpt || '',
          content: articleData.content || '',
          author: articleData.author || 'KGC Innovations',
          category: articleData.category || '',
          tags: Array.isArray(articleData.tags) 
            ? articleData.tags 
            : [],
          featured_image: articleData.featured_image || '',
          gallery_images: Array.isArray(articleData.gallery_images)
            ? articleData.gallery_images.join(', ')
            : (articleData.gallery_images || ''),
          featured: Boolean(articleData.featured),
          status: articleData.status || 'published',
          published_at: articleData.published_at ? 
            new Date(articleData.published_at).toISOString().slice(0, 16) : '',
          meta_title: articleData.meta_title || '',
          meta_description: articleData.meta_description || ''
        });
        setSlugManuallyEdited(true);
      } else {
        setError('Failed to load article data: ' + result.error);
      }
    } catch (err) {
      console.error('Error loading article data:', err);
      setError('Failed to load article data');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title if not manually edited
    if (name === 'title' && !slugManuallyEdited) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }

    // Mark slug as manually edited if user changes it
    if (name === 'slug') {
      setSlugManuallyEdited(true);
    }

    // Auto-generate meta_title from title if empty
    if (name === 'title' && !formData.meta_title) {
      setFormData(prev => ({
        ...prev,
        meta_title: value ? `${value} | KGC Innovations` : ''
      }));
    }

    // Auto-generate meta_description from excerpt if empty
    if (name === 'excerpt' && !formData.meta_description) {
      setFormData(prev => ({
        ...prev,
        meta_description: value
      }));
    }

    // Clear error when user starts typing
    if (error) setError('');
  };

  // Handle content change from rich text editor
  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await fetch('https://cdn.kgcinnovations.com/upload.php', {
        method: 'POST',
        body: uploadFormData,
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          featured_image: result.data.url
        }));
        showNotification('Image uploaded successfully!', 'success');
      } else {
        showNotification('Error uploading image: ' + result.error, 'error');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      showNotification('Failed to upload image. Please try again.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError('');

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        tags: formData.tags, // Already an array
        gallery_images: formData.gallery_images
          .split(',')
          .map(img => img.trim())
          .filter(img => img),
        featured: formData.featured ? 1 : 0,
        published_at: formData.published_at || null
      };

      const url = article 
        ? `/api/news.php?id=${article.id}`
        : '/api/news.php';
      
      const method = article ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();
      
      if (result.success) {
        setShowSuccessOverlay(true);
        // Wait 2 seconds before closing the modal
        setTimeout(() => {
          setShowSuccessOverlay(false);
          onSaveSuccess();
        }, 2000);
      } else {
        setError('Error saving article: ' + (result.error || 'Unknown error'));
      }
      
    } catch (err) {
      console.error('Error saving article:', err);
      setError('Failed to save article. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleCustomTagAdd = () => {
    if (customTag.trim()) {
      addTag(customTag.trim());
      setCustomTag('');
    }
  };

  const handleCustomTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomTagAdd();
    }
  };

  const categories = [
    { value: '', label: 'Select Category' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Strategy', label: 'Strategy' },
    { value: 'Compliance', label: 'Compliance' },
    { value: 'Design', label: 'Design' },
    { value: 'Development', label: 'Development' },
    { value: 'News', label: 'Company News' },
    { value: 'Tutorial', label: 'Tutorial' },
    { value: 'Case Study', label: 'Case Study' },
    { value: 'Industry', label: 'Industry Insights' }
  ];

  const predefinedTags = [
    'web development', 'mobile app', 'react', 'javascript', 'typescript',
    'ui/ux design', 'accessibility', 'eaa compliance', 'digital transformation',
    'strategy', 'best practices', 'tutorial', 'guide', 'tips',
    'trends', '2025', 'innovation', 'technology', 'business',
    'wordpress', 'e-commerce', 'seo', 'performance', 'security'
  ];

  return (
    <>
      <NotificationPopup 
        notification={notification} 
        hideNotification={hideNotification} 
      />
      
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white mb-20">
          {/* Success Overlay */}
          {showSuccessOverlay && (
            <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-10 rounded-md">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {article ? 'Article Updated!' : 'Article Created!'}
                </h3>
                <p className="text-gray-600">
                  {article ? 'Your article has been successfully updated.' : 'Your article has been successfully created.'}
                </p>
              </div>
            </div>
          )}

          {/* Modal Header */}
          <div className="flex justify-between items-center pb-3 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {article ? 'Edit Article' : 'Add New Article'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Modal Body */}
          <div className="mt-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              
              {/* Main Content - Left Side */}
              <div className="lg:col-span-3 space-y-6">
                
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter article title"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="url-friendly-slug"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate from title</p>
                </div>

                {/* Excerpt */}
                <div>
                  <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                    Excerpt
                  </label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief description for article listings and meta description"
                  />
                </div>

                {/* Rich Text Content Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={handleContentChange}
                    placeholder="Start writing your article content here..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Use the toolbar above for formatting. You can add headings, lists, links, images, and more.</p>
                </div>

                {/* SEO Section */}
                <div className="border-t pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">SEO Settings</h4>
                  
                  <div className="space-y-4">
                    {/* Meta Title */}
                    <div>
                      <label htmlFor="meta_title" className="block text-sm font-medium text-gray-700 mb-1">
                        Meta Title
                      </label>
                      <input
                        type="text"
                        id="meta_title"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleInputChange}
                        maxLength="60"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="SEO title for search engines"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.meta_title.length}/60 characters. Auto-generated from title if empty.
                      </p>
                    </div>

                    {/* Meta Description */}
                    <div>
                      <label htmlFor="meta_description" className="block text-sm font-medium text-gray-700 mb-1">
                        Meta Description
                      </label>
                      <textarea
                        id="meta_description"
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleInputChange}
                        rows={2}
                        maxLength="160"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Description for search engine results"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.meta_description.length}/160 characters. Auto-generated from excerpt if empty.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar - Right Side */}
              <div className="space-y-6">
                
                {/* Publish Settings */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Publish Settings</h4>
                  
                  {/* Status */}
                  <div className="mb-4">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  {/* Published Date */}
                  <div className="mb-4">
                    <label htmlFor="published_at" className="block text-sm font-medium text-gray-700 mb-1">
                      Publish Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      id="published_at"
                      name="published_at"
                      value={formData.published_at}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to publish immediately</p>
                  </div>

                  {/* Featured */}
                  <div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="featured"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                        Featured Article
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Featured articles appear prominently on the website</p>
                  </div>
                </div>

                {/* Category & Author */}
                <div className="space-y-4">
                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Author */}
                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                      Author
                    </label>
                    <input
                      type="text"
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Author name"
                    />
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  
                  {/* Selected Tags */}
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border border-gray-300 rounded-md bg-gray-50">
                      {formData.tags.length > 0 ? (
                        formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500 text-sm">No tags selected</span>
                      )}
                    </div>
                  </div>

                  {/* Predefined Tags */}
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 mb-2">Click to add:</p>
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 border border-gray-200 rounded-md bg-white">
                      {predefinedTags
                        .filter(tag => !formData.tags.includes(tag))
                        .map((tag, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => addTag(tag)}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                          >
                            + {tag}
                          </button>
                        ))}
                    </div>
                  </div>

                  {/* Custom Tag Input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customTag}
                      onChange={(e) => setCustomTag(e.target.value)}
                      onKeyPress={handleCustomTagKeyPress}
                      placeholder="Add custom tag..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleCustomTagAdd}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Featured Image */}
                <div>
                  <label htmlFor="featured_image" className="block text-sm font-medium text-gray-700 mb-1">
                    Featured Image
                  </label>
                  <div className="space-y-2">
                    <input
                      type="url"
                      id="featured_image"
                      name="featured_image"
                      value={formData.featured_image}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">or</span>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleImageUpload(file);
                          }}
                          className="hidden"
                          disabled={isUploading}
                        />
                        <span className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          {isUploading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Upload Image
                            </>
                          )}
                        </span>
                      </label>
                    </div>
                    {formData.featured_image && (
                      <div className="mt-2">
                        <img 
                          src={formData.featured_image} 
                          alt="Preview" 
                          className="h-32 w-full object-cover rounded-md border border-gray-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Gallery Images */}
                <div>
                  <label htmlFor="gallery_images" className="block text-sm font-medium text-gray-700 mb-1">
                    Gallery Images
                  </label>
                  <textarea
                    id="gallery_images"
                    name="gallery_images"
                    value={formData.gallery_images}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/img1.jpg, https://example.com/img2.jpg (comma-separated URLs)"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  article ? 'Update Article' : 'Create Article'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsModal;